import { Component, ElementRef, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, UntypedFormGroup } from '@angular/forms';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { DonkiProduct, DonkiStock } from 'src/app/models/donki-product';
import { RoutingService } from 'src/app/services/routing.service';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import { DonkiProductsGQL, DonkiProductsQueryVariables } from 'src/gql/generated';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { SpreadsheetStockBadgeComponent } from '../spreadsheet-stock-badge/spreadsheet-stock-badge.component';
import { KeepaGraphComponent } from '../parts/keepa-graph/keepa-graph.component';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { StorePresetDialogComponent } from '../store-preset-dialog/store-preset-dialog.component';
import { StorePreset } from '../../services/store-preset.service';
import { loadAreaUsage, noteAreaUsage, selectedAreaLabels, withFavoriteAreas } from 'src/app/utils/favorite-areas';
import { isSoldOutByPurchase } from 'src/app/utils/purchase-state';

interface SelectItem {
  label: string;
  value: string; // Assuming IDs are strings
}

interface GroupedSelectItems {
  label: string;
  value: string; // Corresponds to areaId for filtering stores or a general group identifier
  items: SelectItem[];
}

@Component({
  selector: 'app-donki',
  templateUrl: './donki.component.html',
  styleUrls: ['./donki.component.sass'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    MultiSelectModule,
    SelectModule,
    CheckboxModule,
    PanelModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    SpreadsheetStockBadgeComponent,
    KeepaGraphComponent,
    DatePipe,
    IconFieldModule,
    InputIconModule,
    StorePresetDialogComponent,
  ],
})
export class DonkiComponent {
  searchText: string;
  form: UntypedFormGroup;
  isLoading: boolean;

  products: Array<DonkiProduct> = [];
  allProducts: Array<DonkiProduct> = [];
  showPurchased = false;
  showDisplay = false;
  showFound = false;
  hideHigherPricedStores = false;
  isTestAccount = false;

  canRegisterNewItems = true;

  finishedBuildStores = false;
  selectedArea: string[] = [];
  groupedArea: Array<{ label: string; value: string; items: SelectItem[] }> = [];
  allStores: GroupedSelectItems[] = [];
  selectedStores: string[] = [];
  groupedStores: GroupedSelectItems[] = [];

  queueItems: any[] = [];

  registrationDateOptions = [
    { name: '7日以内', value: 7 },
    { name: '14日以内', value: 14 },
    { name: '30日以内', value: 30 },
    { name: '60日以内', value: 60 },
    { name: '全て', value: 0 },
  ];
  filterRegistrationDate = { name: '30日以内', value: 30 };

  splitStr = '::';
  selectedAreaKey = 'savedDonkiSelectedAreaKey';
  selectedStoreKey = 'savedDonkiSelectedStoreKey';

  constructor(
    private elementRef: ElementRef,
    private subtitleService: SubtitleService,
    private routingService: RoutingService,
    private userService: UserService,
    private getGql: DonkiProductsGQL,
  ) {
    this.isLoading = false;
    this.routingService.loginRequired();
    this.userService.donkiRequired();
    this.isTestAccount = this.userService.getEmail() === 'test@rakujan.com';
    this.subtitleService.setSubTitle('ドンキ');
  }

  async ngOnInit() {
    const storedArea = localStorage.getItem(this.selectedAreaKey);
    this.selectedArea = storedArea ? JSON.parse(storedArea) : [];
    const storedStores = localStorage.getItem(this.selectedStoreKey);
    this.selectedStores = storedStores ? JSON.parse(storedStores) : [];
    await this.loadStocks();
  }

  async loadStocks() {
    var regDate =
      this.filterRegistrationDate?.value && this.filterRegistrationDate?.value > 0
        ? moment().subtract(this.filterRegistrationDate.value, 'days').format('YYYY/MM/DD')
        : null;
    const resp = await firstValueFrom(
      this.getGql.fetch({ variables: { registrationDate: regDate } as DonkiProductsQueryVariables }),
    );
    if (!resp.data || !resp.data.donkiProducts) {
      this.products = [];
      this.allProducts = [];
      // Handle error or empty state
      return;
    }
    this.products = resp.data.donkiProducts.map((p) => {
      var hasAnyStock = false;
      const stocks = p.stocks.map((s) => {
        hasAnyStock =
          hasAnyStock ||
          !isSoldOutByPurchase({
            isPurchased: s.operation?.isPurchased || false,
            purchasedQuantity: s.operation?.purchasedQuantity ?? null,
            stockNum: s.stockNum,
          });
        return {
          type: 'donki',
          id: s.id,
          createdAt: s.createdAt,
          isPurchased: s.operation?.isPurchased || false,
          purchasedUserName: s.operation?.purchasedUser?.name || '',
          purchasedAt: s.operation?.purchasedAt || '',
          purchasedQuantity: s.operation?.purchasedQuantity ?? null,
          isDisplay: s.operation?.isDisplay || false,
          displayUserName: s.operation?.displayUser?.name || '',
          displaySetAt: s.operation?.displaySetAt || '',
          isNotFound: s.operation?.isNotFound || false,
          notFoundUserName: s.operation?.notFoundUser?.name || '',
          notFoundAt: s.operation?.notFoundAt || '',
          isRestricted: s.operation?.isRestricted || false,
          restrictedUserName: s.operation?.restrictedUser?.name || '',
          restrictedAt: s.operation?.restrictedAt || '',
          isSkipped: s.operation?.isSkipped || false,
          skippedUserName: s.operation?.skippedUser?.name || '',
          skippedAt: s.operation?.skippedAt || '',
          isStaffNoStock: s.operation?.isStaffNoStock || false,
          staffNoStockUserName: s.operation?.staffNoStockUser?.name || '',
          staffNoStockAt: s.operation?.staffNoStockAt || '',
          isConfirmed: s.operation?.isConfirmed || false,
          confirmedUserName: s.operation?.confirmedUser?.name || '',
          confirmedAt: s.operation?.confirmedSetAt || '',
          memo: s.operation?.memo,
          memoUserName: s.operation?.memoUser?.name || '',
          memoAddedAt: s.operation?.memoAddedAt || '',
          stockNum: s.stockNum,
          price: s.price,
          storeId: s.store.id,
          storeName: s.store.name,
          areaId: s.store.area.id,
          areaName: s.store.area.name,
        } as DonkiStock;
      });

      const sp = p.product;
      const mainAp = sp.amazonProducts.find((ap) => ap.isMain);
      const ap = mainAp ?? (sp.amazonProducts.length > 0 ? p.product.amazonProducts[0] : null);
      return {
        id: p.id,
        jan: sp.jan,
        asin: ap?.asin || '',
        amazonPrice: ap?.latestPrice || -1,
        imageUrl: ap?.imageUrl || '',
        boughtPastMonth: ap?.keepaProduct?.boughtPastMonth,
        totalStocks: p.totalStocks,
        registrationDate: p.registrationDate,
        name: ap?.name || '',
        hasAnyStock: hasAnyStock,
        stocks: stocks,
        filteredStocks: this.doFilters(stocks, ap?.latestPrice || -1),
      };
    });

    this.allProducts = [...this.products];

    var area: { label: string; value: string; items: SelectItem[] } = {
      label: '都道府県',
      value: 'area',
      items: [],
    };
    this.groupedStores = [];

    if (resp.data && resp.data.storeAreas) {
      resp.data.storeAreas.forEach((sa) => {
        if (sa) {
          // Null check for sa
          area.items.push({
            label: sa.name,
            value: sa.id,
          });

          const store: GroupedSelectItems = {
            label: sa.name,
            value: sa.id,
            items: [],
          };
          if (sa.donkiStores) {
            sa.donkiStores.forEach((s) => {
              if (s) {
                // Null check for s
                store.items.push({
                  label: s.name,
                  value: s.id,
                });
              }
            });
          }
          this.groupedStores.push(store);
        }
      });
    }

    this.allStores = [...this.groupedStores];
    // よく使う都道府県（このブラウザで選んだ回数の多い順に 5 件）を先頭に出す（2026-10-15 ユーザー指定）
    this.groupedArea = withFavoriteAreas([area], loadAreaUsage());

    if (this.selectedArea.length > 0) {
      this.doChangeArea(null);
    }
    if (this.selectedStores.length > 0) {
      this.doChangeStores(null);
    }
  }

  async doChangeRegistrationDate($e: any) {
    await this.loadStocks();
  }

  async doChangeShowPurchased($e: any) {
    await this.loadStocks();
  }

  clearArea() {
    this.selectedArea = [];
    this.doChangeArea(null);
  }

  clearStores() {
    this.selectedStores = [];
    this.doChangeStores(null);
  }

  onPresetLoaded(preset: StorePreset) {
    this.selectedArea = preset.selectedArea || [];
    this.selectedStores = preset.selectedStores || [];
    this.rememberAreaUsage();
    this.doChangeArea(null);
    this.doChangeStores(null);
  }

  /** 選んだ都道府県の回数を覚えて、「よく使う」グループを並べ直す */
  rememberAreaUsage() {
    const usage = noteAreaUsage(selectedAreaLabels(this.groupedArea, this.selectedArea));
    this.groupedArea = withFavoriteAreas(this.groupedArea, usage);
  }

  doChangeArea($e: any) {
    localStorage.setItem(this.selectedAreaKey, JSON.stringify(this.selectedArea));
    if ($e) this.rememberAreaUsage();
    this.products = [];
    this.allProducts.forEach((p) => {
      p.filteredStocks = this.doFilters(p.stocks, p.amazonPrice);
      if (p.filteredStocks.length > 0) {
        this.products.push(p);
      }
    });
    this.groupedStores = this.allStores.filter((s: GroupedSelectItems) => {
      return this.selectedArea.includes(s.value);
    });
  }

  doChangeStores($e: any) {
    localStorage.setItem(this.selectedStoreKey, JSON.stringify(this.selectedStores));
    this.products = [];
    this.allProducts.forEach((p) => {
      p.filteredStocks = this.doFilters(p.stocks, p.amazonPrice);
      if (p.filteredStocks.length > 0) {
        this.products.push(p);
      }
    });
  }

  doFilters(stocks: DonkiStock[], amazonPrice: number) {
    stocks = this.doFilterStocks(stocks);
    stocks = this.doFilterArea(stocks);
    stocks = this.doFilterStores(stocks);
    stocks = this.doFilterHigherPricedStores(stocks, amazonPrice);
    return stocks;
  }

  private doFilterStocks(stocks: DonkiStock[]) {
    if (stocks.length === 0) {
      return stocks;
    }
    const needsToFilter = !this.showPurchased || this.showFound;
    if (needsToFilter) {
      return stocks.filter((s) => {
        if (!this.showPurchased) {
          // 購入で売り切れた店だけ隠す。在庫が残る店（5 個中 3 個購入など）は出す
          if (isSoldOutByPurchase(s)) {
            return false;
          }
        }
        if (this.showFound) {
          // 店頭無・店員無（どちらも「その店に無かった」）を隠す
          if (s.isNotFound || s.isStaffNoStock) {
            return false;
          }
        }
        return true;
      });
    }
    return stocks;
  }

  private doFilterArea(stocks: DonkiStock[]) {
    if (this.selectedArea.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter pref and store nmaes
    return stocks.filter((s) => {
      return this.selectedArea.includes(s.areaId);
    });
  }

  private doFilterStores(stocks: DonkiStock[]) {
    if (this.selectedStores.length === 0 || stocks.length === 0) {
      return stocks;
    }
    // filter pref and store nmaes
    return stocks.filter((s) => {
      return this.selectedStores.includes(s.storeId);
    });
  }

  private doFilterHigherPricedStores(stocks: DonkiStock[], amazonPrice: number) {
    if (!this.hideHigherPricedStores || stocks.length === 0 || amazonPrice <= 0) {
      return stocks;
    }
    // Amazon価格より高い店舗データを除外
    return stocks.filter((s) => {
      return s.price <= amazonPrice;
    });
  }

  async reloadStocks($e: any) {
    // Consider a more specific event type if applicable
    await this.loadStocks();
  }

  onHideHigherPricedStoresChange($e: any) {
    this.hideHigherPricedStores = !!$e.checked;
    this.applyFilters();
  }

  private applyFilters() {
    this.products = [];
    this.allProducts.forEach((p) => {
      p.filteredStocks = this.doFilters(p.stocks, p.amazonPrice);
      if (p.filteredStocks.length > 0) {
        this.products.push(p);
      }
    });
  }

  getRowStyle(product: DonkiProduct) {
    if (!product.hasAnyStock) {
      if (this.showPurchased) {
        return 'unpublic';
      }
      return 'hide-table-row';
    }
    const isAllPurchased = product.filteredStocks.filter((s) => s.isPurchased).length === 0;
    if (isAllPurchased) {
      if (this.showPurchased) {
        return 'unpublic';
      }
      return 'hide-table-row';
    }

    return '';
  }

  downloadCsv(table: any) {
    // Consider a more specific type for 'table' if it comes from a specific component like p-table
    const productsToProcess: DonkiProduct[] = table?.filteredValue ?? this.products;
    const values = productsToProcess.filter((p) => p.filteredStocks && p.filteredStocks.length > 0);
    const csv = values
      .flatMap((p) => {
        const registrationDateFormatted = moment(p.registrationDate).format('MMDD');

        const priceMap = new Map<number, { storeName: string; stockNum: number }[]>();

        p.filteredStocks.forEach((s) => {
          if (!priceMap.has(s.price)) {
            priceMap.set(s.price, []);
          }
          const storesAtPrice = priceMap.get(s.price);
          if (storesAtPrice) {
            storesAtPrice.push({
              storeName: s.storeName,
              stockNum: s.stockNum,
            });
          }
        });

        return Array.from(priceMap.entries()).map(
          ([price, storeInfos]: [number, { storeName: string; stockNum: number }[]]) => {
            const storeInfosText = storeInfos
              .map((info: { storeName: string; stockNum: number }) => `${info.storeName}:${info.stockNum}`)
              .join(',');
            const totalStock = p.totalStocks != null ? p.totalStocks : '-';
            const boughtPastMonth = p.boughtPastMonth != null ? p.boughtPastMonth : '-';
            const memo = `${registrationDateFormatted}_総在${totalStock}_先販${boughtPastMonth}_${storeInfosText}`;
            return `${p.jan},${price},1,,"${memo}"`;
          },
        );
      })
      .join('\n');
    this.outputCsv(csv, 'donki.csv');
  }

  private outputCsv(csv: string, filename: string) {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const elem = this.elementRef.nativeElement;
    const link = elem.querySelector('#csv-donwload') as HTMLAnchorElement;
    link.href = url;
    link.download = filename;
    link.click();
  }
}
